/**
 * Page Transition Animations
 *
 * Reusable Framer Motion variants for smooth page transitions throughout the app.
 * Provides consistent animation behavior across all screens.
 */

import { Variants } from 'framer-motion';

/**
 * Fade and slide transition (default)
 * Slides in from right, fades out to left
 * Best for: Sequential flows (booking process, onboarding)
 */
export const fadeSlideVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1], // Cubic bezier easing
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1],
    },
  },
};

/**
 * Fade only transition
 * Simple opacity fade
 * Best for: Modal overlays, quick switches
 */
export const fadeVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeIn',
    },
  },
};

/**
 * Scale and fade transition
 * Scales up while fading in
 * Best for: Success screens, important announcements
 */
export const scaleVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1],
    },
  },
};

/**
 * Slide up transition
 * Slides up from bottom
 * Best for: Mobile-style modals, bottom sheets
 */
export const slideUpVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1],
    },
  },
};

/**
 * RTL-aware slide transition
 * Slides from left for RTL languages
 * Best for: Hebrew/Arabic interfaces
 */
export const fadeSlideRTLVariants: Variants = {
  initial: {
    opacity: 0,
    x: -20, // Reversed for RTL
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    x: 20, // Reversed for RTL
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1],
    },
  },
};

/**
 * Default transition config
 * Use this for consistent timing across the app
 */
export const defaultTransition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1] as const,
};

/**
 * Fast transition for quick interactions
 */
export const fastTransition = {
  duration: 0.15,
  ease: 'easeOut' as const,
};

/**
 * Slow transition for emphasis
 */
export const slowTransition = {
  duration: 0.5,
  ease: [0.4, 0, 0.2, 1] as const,
};
